import { OrderCategory } from '@contracts/order/OrderCategory';
import type { TransactionCurrency } from '@contracts/payment/Currency';
import type { ChangeRequestList } from '@src/changeRequest/domain/ChangeRequestList';
import type { Timestamp } from '@src/common/time/Timestamp';
import type { ClientOrderSubmissionState } from '@src/orders/domain/ClientOrderSubmissionState';
import { OrderStatus } from '@src/orders/domain/Order';
import type { PermitSummary } from '@src/orders/domain/PermitSummary';
import { PreauthStatus } from '@src/orders/domain/PreauthStatus';

export interface OrderDetails {
  leadTrailerUnit?: number;
  tractorUnit?: number;
  tractorLicensePlate?: string;
  leadTrailerLicensePlate?: string;
  effectiveDate: Timestamp;
  submittedDate: Timestamp;
  payment: {
    status: PreauthStatus;
    settledAmount: number;
    currency?: TransactionCurrency;
  };
  PONumber?: string;
  createdBy?: string;
  changeRequests?: ChangeRequestList;
}

export enum OrderActions {
  DELETE = 'DELETE',
  EDIT = 'EDIT',
  TOGGLE_ON_HOLD = 'TOGGLE_ON_HOLD',
  CANCEL = 'CANCEL',
  ADD_PERMIT = 'ADD_PERMIT',
  REVIEW = 'REVIEW',
  MODIFY_INVOICE = 'MODIFY_INVOICE',
  VIEW_INVOICE = 'VIEW_INVOICE',
  REOPEN = 'REOPEN',
  DUPLICATE_PERMITS = 'DUPLICATE_PERMITS',
  VIEW = 'VIEW',
  REMOVE_PERMITS = 'REMOVE_PERMITS',
  DUPLICATE_ORDER = 'DUPLICATE_ORDER',
  CHANGE_REQUEST = 'CHANGE_REQUEST',
  FORCE_TO_REVIEW = 'FORCE_TO_REVIEW',
  GENERATE_PDF = 'GENERATE_PDF',
  UPDATE_BILL_TO = 'UPDATE_BILL_TO',
}

interface PermitCountByCategory {
  priority: number;
  regular: number;
  awaiting: number;
}

export interface OrderListingAttributes {
  id: string;
  displayId: string;
  billToClientId: string;
  client: {
    id: string;
    name: string;
    doingBusinessAs?: string;
    isTemporary?: boolean;
  };
  isActive: boolean;
  requiresAttention: boolean;
  status: OrderStatus;
  category: OrderCategory;
  claimedBy?: string;
  createdBy?: string;
  permitSummaryGroups: PermitSummaryGroup[];
  details: OrderDetails;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  allowedActions: Set<OrderActions>;
  permitCounts: PermitCountByCategory;
  clientSubmissionState?: ClientOrderSubmissionState;
}

export interface PermitSummaryGroup {
  originalPermit: PermitSummary;
  subsequentPermits: PermitSummary[];
}

export interface ClientSummary {
  id: string;
  name: string;
  doingBusinessAs?: string;
  isAgency?: boolean;
  isTemporary?: boolean;
  responsibleAgency?: ClientSummary;
}

export class OrderListing {
  public readonly id: string;
  public readonly displayId: string;
  public readonly client: ClientSummary;
  public readonly billToClientId: string;
  public readonly isActive: boolean;
  public readonly requiresAttention: boolean;
  public readonly status: OrderStatus;
  public readonly claimedBy?: string;
  public readonly permitSummaryGroups: PermitSummaryGroup[];
  public readonly details: OrderDetails;
  public readonly createdAt: Timestamp;
  public readonly updatedAt: Timestamp;
  public readonly permitCounts: PermitCountByCategory;
  public readonly category: OrderCategory;
  public readonly allowedActions: Set<OrderActions>;
  readonly clientSubmissionState?: ClientOrderSubmissionState;

  constructor(attributes: OrderListingAttributes) {
    this.id = attributes.id;
    this.displayId = attributes.displayId;
    this.client = attributes.client;
    this.isActive = attributes.isActive;
    this.requiresAttention = attributes.requiresAttention;
    this.status = attributes.status;
    this.claimedBy = attributes.claimedBy;
    this.permitSummaryGroups = attributes.permitSummaryGroups;
    this.details = attributes.details;
    this.createdAt = attributes.createdAt;
    this.updatedAt = attributes.updatedAt;
    this.permitCounts = attributes.permitCounts;
    this.allowedActions = attributes.allowedActions;
    this.billToClientId = attributes.billToClientId;
    this.clientSubmissionState = attributes.clientSubmissionState;
    this.category = attributes.category;
  }

  canBeEditedBy(userId?: string): boolean {
    return this.claimedBy === undefined || this.claimedBy === userId;
  }

  canDo(action: OrderActions): boolean {
    return this.allowedActions.has(action);
  }

  isBillable() {
    return ![
      PreauthStatus.PreauthorizationRequired,
      PreauthStatus.MissingPaymentMethod,
    ].includes(this.details.payment.status);
  }

  isCompleted() {
    return this.status === OrderStatus.COMPLETED;
  }

  isArchived() {
    return this.status === OrderStatus.ARCHIVED;
  }

  getAllPermits() {
    return this.permitSummaryGroups.flatMap((group) => [
      group.originalPermit,
      ...group.subsequentPermits,
    ]);
  }

  getPermit(permitId: string): PermitSummary | undefined {
    return this.getAllPermits().find((permit) => permit.id === permitId);
  }

  wasSubmittedByClient(): boolean {
    return this.clientSubmissionState !== undefined;
  }

  isInNewCategory(): boolean {
    return this.category === OrderCategory.new;
  }

  getLastUpdatedFor(field: string) {
    if (!this.details.changeRequests) {
      return undefined;
    }
    return this.details.changeRequests.getLastChangeDateFor(field);
  }
}

export class OrderList {
  constructor(
    public readonly orders: OrderListing[],
    public readonly totalOrders: number,
    public readonly totalPages: number,
  ) {}
}
